<template>
	<div class="container-fluid p-4">
		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div class="card mb-4">
			<div class="card-body">
				<h2 class="h5 mb-0">Register devices</h2>
				<p class="text-muted mb-3 small">
					One serial per line, under one catalog trap type. Serials are never
					reused.
				</p>
				<form
					class="row g-3"
					style="max-width: 720px"
					@submit.prevent="register"
				>
					<div class="col-12">
						<label class="form-label" for="deviceCatalogType"
							>Catalog trap type</label
						>
						<select
							id="deviceCatalogType"
							v-model="batchCatalogType"
							class="form-select"
							required
						>
							<option value="" disabled>
								{{
									enabledCatalog.length === 0
										? "Enable a trap type on the Configuration tab first"
										: "Choose a catalog type"
								}}
							</option>
							<option
								v-for="entry in enabledCatalog"
								:key="entry.trap_type_id"
								:value="entry.trap_type_id"
							>
								{{ entry.name }}
							</option>
						</select>
					</div>
					<div class="col-12">
						<div class="d-flex align-items-center gap-2 mb-1">
							<label class="form-label mb-0" for="deviceSerials">Serials</label>
							<button
								type="button"
								class="btn btn-sm btn-outline-secondary"
								title="Generate a serial number"
								@click="addGeneratedSerial"
							>
								<i class="bi bi-plus-lg me-1"></i>Generate
							</button>
							<button
								type="button"
								class="btn btn-sm btn-link p-0 text-secondary"
								aria-label="About generated serials"
								title="About generated serials"
								@click="serialHelpOpen = true"
							>
								<i class="bi bi-question-circle"></i>
							</button>
						</div>
						<textarea
							id="deviceSerials"
							v-model="batchSerials"
							class="form-control"
							rows="3"
							:readonly="!requiresManufacturerSerial"
							:placeholder="
								requiresManufacturerSerial
									? 'One serial per line'
									: 'Generated by Nidus, one serial per device'
							"
							required
						/>
						<div class="form-text">
							<template v-if="requiresManufacturerSerial">
								One serial per line, as printed on the device. Use Generate for
								a unit whose label is missing or unreadable.
							</template>
							<template v-else>
								This trap type has no manufacturer serial, so Nidus generates
								one per device. Generate adds another device's serial.
							</template>
						</div>
						<div
							v-if="!requiresManufacturerSerial && !organizationSlug"
							class="form-text text-danger"
						>
							Your district has no slug, so Nidus cannot generate a serial. Set
							one on the organization first.
						</div>
					</div>
					<div class="col-12">
						<ButtonLoading
							:loading="saving"
							class="btn btn-primary"
							type="submit"
						>
							Register
						</ButtonLoading>
					</div>
				</form>
			</div>
		</div>

		<div
			v-if="serialHelpOpen"
			class="modal fade show d-block"
			tabindex="-1"
			role="dialog"
			aria-modal="true"
			aria-labelledby="serialHelpTitle"
			@keydown.esc="serialHelpOpen = false"
		>
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h2 id="serialHelpTitle" class="modal-title h5 mb-0">
							Generated serial numbers
						</h2>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="serialHelpOpen = false"
						></button>
					</div>
					<div class="modal-body">
						<p>
							Not every trap comes with a serial number from the manufacturer.
							For those trap types Nidus generates one per device as you
							register it, and the serial list is not typed by hand.
						</p>
						<p>
							A generated serial is your district's slug, a TD designator naming
							it a trap device, then six capital letters and digits:
							<code>{{ serialPrefix }}-TD-XXXXXX</code>. Characters that read
							alike are left out — no 0 or O, no 1 or I, no 2 or Z — so it
							cannot be confused with a sample label's SL sticker.
						</p>
						<p class="mb-0">
							For a type the manufacturer does serialize, the field is typed in
							by hand; Generate adds one only for a unit whose label is missing
							or unreadable.
						</p>
					</div>
					<div class="modal-footer">
						<button
							type="button"
							class="btn btn-outline-secondary"
							@click="serialHelpOpen = false"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
		<div v-if="serialHelpOpen" class="modal-backdrop fade show"></div>

		<div class="card">
			<div class="card-body">
				<div class="d-flex justify-content-between align-items-center mb-3">
					<h2 class="h5 mb-0">The district's devices</h2>
					<button
						v-if="devices.length > 0"
						type="button"
						class="btn btn-outline-secondary btn-sm"
						@click="printStickers"
					>
						<i class="bi bi-printer me-1"></i> Print sticker sheet
					</button>
				</div>
				<div v-if="loading" class="text-muted">Loading…</div>
				<div v-else-if="devices.length === 0" class="text-muted">
					No devices registered yet.
				</div>
				<table v-else class="table align-middle mb-0">
					<thead>
						<tr>
							<th>Serial</th>
							<th>Catalog type</th>
							<th>State</th>
							<th>QR</th>
							<th class="text-end">Change state</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="device in devices" :key="device.id">
							<td>{{ device.serial }}</td>
							<td>{{ catalogName(device.catalog_trap_type_id) }}</td>
							<td>
								<span class="badge text-bg-light">{{
									TRAP_DEVICE_STATE_LABELS[device.state] ?? device.state
								}}</span>
							</td>
							<td>
								<QrCode
									:src="`/api/qr-code/trap-device/${device.id}`"
									:alt="`${device.serial} QR code`"
								/>
							</td>
							<td class="text-end" style="min-width: 220px">
								<select
									class="form-select form-select-sm d-inline-block w-auto me-2"
									:value="device.state"
									@change="
										changeState(
											device,
											($event.target as HTMLSelectElement).value,
										)
									"
								>
									<option
										v-for="(label, state) in TRAP_DEVICE_STATE_LABELS"
										:key="state"
										:value="state"
									>
										{{ label }}
									</option>
								</select>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<!-- The sticker sheet, printed with the browser's own print: each device's
		     QR and serial, one sticker per cell. -->
		<PrintSheet>
			<h2 class="h5 text-center mb-4">Trap device stickers</h2>
			<div class="sticker-grid">
				<div v-for="device in devices" :key="device.id" class="sticker">
					<img
						:src="`/api/qr-code/trap-device/${device.id}`"
						:alt="`${device.serial} QR code`"
						width="160"
						height="160"
					/>
					<div class="sticker-serial">{{ device.serial }}</div>
					<div class="sticker-type">
						{{ catalogName(device.catalog_trap_type_id) }}
					</div>
				</div>
			</div>
		</PrintSheet>

		<ToastNotification
			v-if="toastShow"
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import PrintSheet from "@/components/common/PrintSheet.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import QrCode from "@/components/traps/QrCode.vue";
import { useSessionStore } from "@/store/session";
import { TRAP_DEVICE_STATE_LABELS } from "@/lib/trapState";
import { generateTrapSerial } from "@/lib/trapSerial";
import type {
	TrapDeviceRegisterRequest,
	TrapDeviceView,
	TrapTypeCatalogEntry,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const catalog = ref<TrapTypeCatalogEntry[]>([]);
/** Registration offers only the catalog types this district fields (§31 Rule
 *  1). The whole catalog stays loaded: an existing device may still name a type
 *  the district has since disabled, and the list has to render it. */
const enabledCatalog = computed(() =>
	catalog.value.filter((entry) => entry.enabled),
);
const devices = ref<TrapDeviceView[]>([]);
const loading = ref(false);
const saving = ref(false);
const error = ref("");

const batchCatalogType = ref("");
const batchSerials = ref("");
const serialHelpOpen = ref(false);

const session = useSessionStore();
const organizationSlug = computed(() => session.organization?.slug ?? "");
const serialPrefix = computed(
	() => organizationSlug.value || "<district-slug>",
);

/** The chosen catalog entry, or undefined until a type is picked. */
const selectedCatalogEntry = computed(() =>
	catalog.value.find((entry) => entry.trap_type_id === batchCatalogType.value),
);

/** Whether the chosen type's units carry a manufacturer serial. Until a type is
 *  picked the form asks, so nothing is generated behind the operator's back. */
const requiresManufacturerSerial = computed(
	() => selectedCatalogEntry.value?.requires_manufacturer_serial ?? true,
);

// A serial Nidus invents must not be mistaken for a manufacturer's or a sample
// label's: it is the district's slug, the TD designator, then six characters
// from an alphabet that leaves out the characters a person reads alike (see
// generateTrapSerial).
function generateSerial(): string {
	return generateTrapSerial(organizationSlug.value);
}

/** Generate appends one serial to the list: a second device of a type with no
 *  manufacturer serial, or a unit whose label is missing or unreadable. */
function addGeneratedSerial() {
	if (!organizationSlug.value) {
		error.value =
			"Your district has no slug, so Nidus cannot generate a serial. Set one on the organization first.";
		return;
	}
	error.value = "";
	const lines = batchSerials.value
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line !== "");
	lines.push(generateSerial());
	batchSerials.value = lines.join("\n");
}

/** Choosing a type decides how serials arrive: a type whose units carry a
 *  manufacturer serial is typed in, one that does not is generated. */
watch(batchCatalogType, (slug) => {
	error.value = "";
	const entry = catalog.value.find((item) => item.trap_type_id === slug);
	const generated =
		entry !== undefined &&
		!entry.requires_manufacturer_serial &&
		organizationSlug.value !== "";
	batchSerials.value = generated ? generateSerial() : "";
});

function catalogName(id: string): string {
	return catalog.value.find((entry) => entry.trap_type_id === id)?.name ?? id;
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const [catalogResp, deviceRows] = await Promise.all([
			apiClient.JSONGet<{ entries: TrapTypeCatalogEntry[] }>(
				"/api/trap-type-catalog",
			),
			apiClient.JSONGet<TrapDeviceView[]>("/api/trap-device"),
		]);
		catalog.value = catalogResp.entries;
		devices.value = deviceRows;
	} catch (e: unknown) {
		error.value = e instanceof Error ? e.message : "Failed to load devices.";
	} finally {
		loading.value = false;
	}
}

async function register() {
	saving.value = true;
	error.value = "";
	const serials = batchSerials.value
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line !== "");
	const body: TrapDeviceRegisterRequest = {
		devices: serials.map((serial) => ({
			serial,
			catalog_trap_type_id: batchCatalogType.value,
		})),
	};
	try {
		await apiClient.JSONPost<TrapDeviceView[]>("/api/trap-device", body);
		showNotification(
			"Devices registered",
			`${serials.length} device(s) registered.`,
		);
		// A generated type keeps its list ready for the next batch: one serial,
		// which Generate extends.
		batchSerials.value = requiresManufacturerSerial.value
			? ""
			: organizationSlug.value
				? generateSerial()
				: "";
		await load();
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to register devices.";
	} finally {
		saving.value = false;
	}
}

async function changeState(device: TrapDeviceView, state: string) {
	if (state === device.state) return;
	error.value = "";
	try {
		await apiClient.JSONPatch(`/api/trap-device/${device.id}`, { state });
		showNotification(
			"State changed",
			`${device.serial} is now ${TRAP_DEVICE_STATE_LABELS[state] ?? state}.`,
		);
		await load();
	} catch (e: unknown) {
		error.value =
			e instanceof Error ? e.message : "Failed to change the device's state.";
	}
}

function printStickers() {
	window.print();
}

onMounted(load);
</script>

<style scoped>
.sticker-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1rem;
}
.sticker {
	border: 1px dashed #adb5bd;
	border-radius: 0.5rem;
	padding: 1rem;
	text-align: center;
	break-inside: avoid;
}
.sticker-serial {
	font-weight: 600;
	margin-top: 0.5rem;
}
.sticker-type {
	font-size: 0.85rem;
	color: #6c757d;
}
</style>
